import dot from 'dot-prop-immutable';
import moment from 'moment';
import { denormalize } from 'normalizr';
import { any, path, pathOr, uniq, uniqBy } from 'ramda';
import reduceReducers from 'reduce-reducers';
import { createSelector } from 'reselect';

import { SUPPLIER_ROLES, PERMISSIONS } from 'constants/all';

import { createEntitiesReducer } from 'modules/common/redux';

import { getTeamMembers } from 'store/supplier/selectors';

import { toMoment } from 'utils/dateUtils';
import { createDeepMemoizedSelector } from 'utils/reduxUtils';

import { CHANNEL_TYPES } from '../data/constants';
import { getIsExportControlled } from '../data/reducer';
import { findFarthestExpiration } from '../shares/data/utils';
import { types } from './actions';
import { annotationSchema, channelSchema, shareSchema } from './entities';

const initialState = {
  entities: {},
};

const entitiesReducer = createEntitiesReducer(
  ['annotations', 'channels', 'channelEvents', 'comments', 'shares', 'tasks'],
  [
    types.ANNOTATION_RECEIVE,
    types.CHANNEL_RECEIVE,
    types.CHANNEL_EVENT_RECEIVE,
    types.CHANNELS_RECEIVE,
    types.COMMENT_RECEIVE,
    types.SHARE_RECEIVE,
    types.TASK_RECEIVE,
  ],
);

const reducer = (state = initialState, action) => {
  const { payload, type } = action;
  switch (type) {
    case types.ANNOTATION_RECEIVE: {
      const { channelId } = payload.entities.annotations[payload.result];
      const nextAnnotationsForChannel = uniq(
        pathOr(
          [],
          ['entities', 'channels', channelId, 'annotations'],
          state,
        ).concat([payload.result]),
      );
      return dot.set(
        state,
        `entities.channels.${channelId}.annotations`,
        nextAnnotationsForChannel,
      );
    }
    case types.COMMENT_RECEIVE: {
      const { annotationId } = payload.entities.comments[payload.result];
      const nextCommentsListForAnnotation = uniq(
        pathOr(
          [],
          ['entities', 'annotations', annotationId, 'comments'],
          state,
        ).concat([payload.result]),
      );
      return dot.set(
        state,
        `entities.annotations.${annotationId}.comments`,
        nextCommentsListForAnnotation,
      );
    }
    case types.ENTITIES_STORE_INIT:
      return {
        ...initialState,
      };
    case types.SHARE_RECEIVE: {
      const { channelId } = payload.entities.shares[payload.result];
      const nextShareListForChannel = uniq(
        pathOr([], ['entities', 'channels', channelId, 'shares'], state).concat(
          [payload.result],
        ),
      );
      return dot.set(
        state,
        `entities.channels.${channelId}.shares`,
        nextShareListForChannel,
      );
    }
    case types.CHANNEL_EVENT_RECEIVE: {
      const { channelId } = payload.entities.channelEvents[payload.result];
      const nextChannelEventListForChannel = uniq(
        pathOr(
          [],
          ['entities', 'channels', channelId, 'channelEvents'],
          state,
        ).concat([payload.result]),
      );
      return dot.set(
        state,
        `entities.channels.${channelId}.channelEvents`,
        nextChannelEventListForChannel,
      );
    }
    default:
      return state;
  }
};

const getScope = (state) => state.modules.common.chat.data;

export const getChannelById = (state, id = null) => {
  if (id) {
    return denormalize(id, channelSchema, getScope(state).entities);
  }
};

export const channelHasActiveShares = (state, channelId) => {
  const shares = pathOr([], ['shares'], getChannelById(state, channelId));
  return shares.some((share) => share.accessRevokedOn == null);
};

export const getAnnotationById = (state, id) =>
  denormalize(id, annotationSchema, getScope(state).entities);

export const getCommentById = (state, id) =>
  pathOr(null, ['comments', id], getScope(state).entities);

export const getChannelStream = createDeepMemoizedSelector(
  (state, id) => pathOr([], ['annotations'], getChannelById(state, id)),
  (state, id) => pathOr([], ['channelEvents'], getChannelById(state, id)),
  (state, id) => pathOr([], ['shares'], getChannelById(state, id)),
  (annotations, channelEvents, shares) => {
    const revokedShares = shares
      .filter((share) => share.accessRevokedOn !== null)
      .map((revokedShare) => ({ ...revokedShare, isRevokedShare: true }));
    const stream = annotations.concat(shares, revokedShares, channelEvents);
    return stream.sort((a, b) => {
      const aDate = a.isRevokedShare
        ? toMoment(a.accessRevokedOn)
        : toMoment(a.created);
      const bDate = b.isRevokedShare
        ? toMoment(b.accessRevokedOn)
        : toMoment(b.created);
      return aDate - bDate;
    });
  },
);

export const getNormalizedSecondaryChannels = (state) =>
  Object.values(pathOr({}, ['entities', 'channels'], getScope(state))).filter(
    ({ type }) => type === CHANNEL_TYPES.SECONDARY,
  );

export const getFirstSecondaryChannelId = (state) =>
  getNormalizedSecondaryChannels(state)[0]?.id;

export const getNormalizedChannelById = (state, id = null) => {
  if (id) {
    return pathOr(null, ['entities', 'channels', id], getScope(state));
  }
};

// returns a bool map associating each user's id with their controlled access
const getTeamMembersCuiAccessMap = (state) => {
  const teamMembers = getTeamMembers(state);
  const accessMap = new Map();
  for (const teamMember of teamMembers) {
    const roles = teamMember?.supplierRoles;
    accessMap[teamMember.userId] = roles
      ? roles[SUPPLIER_ROLES.CONTROLLED_INFO] !== PERMISSIONS.NO_ACCESS
      : true;
  }
  return accessMap;
};

// returns an array of all team members or those who have access
// if channel is designated as controlled (CUI)
export const getTeamMembersForChannel = (state) => {
  const isChannelExportControlled = getIsExportControlled(state);
  const teamMemberAccessMap = getTeamMembersCuiAccessMap(state);
  if (!isChannelExportControlled) {
    return getTeamMembers(state);
  }

  return getTeamMembers(state).filter(
    (teamMember) => teamMemberAccessMap[teamMember.userId],
  );
};

// returns an array of all users who have commented in a channel or been @mentioned
// and have access if channel is designated as controlled (CUI)
export const getActiveUsersForChannel = (state, id = null) => {
  if (id) {
    // get all annotations belonging to this channel
    const annotationIds = pathOr(
      [],
      ['annotations'],
      getNormalizedChannelById(state, id),
    );

    // if channel is controlled (CUI), exclude users who don't have access
    const isChannelExportControlled = getIsExportControlled(state);
    const teamMemberAccessMap = getTeamMembersCuiAccessMap(state);

    const comments = Object.values(
      pathOr({}, ['entities', 'comments'], getScope(state)),
    ).filter(({ annotationId }) => annotationIds.includes(annotationId));

    const activeUsers = [];
    comments.forEach((comment) => {
      // add the comments author
      if (pathOr(true, ['isCreatedByActive'], comment)) {
        if (isChannelExportControlled) {
          // unless they have explicit NO ACCESS
          if (teamMemberAccessMap[comment.createdById]) {
            activeUsers.push({
              fullName: comment.createdByFullName,
              userId: comment.createdById,
            });
          }
        } else {
          activeUsers.push({
            fullName: comment.createdByFullName,
            userId: comment.createdById,
          });
        }
      }
      // add @mentioned team mates
      comment.content.forEach((content) => {
        if (
          content.type === 'mention' &&
          pathOr(true, ['isMentionedUserActive'], content)
        ) {
          if (isChannelExportControlled) {
            // unless they have explicit NO ACCESS
            if (teamMemberAccessMap[content.userId]) {
              activeUsers.push({
                fullName: content.content,
                userId: content.userId,
              });
            }
          } else {
            activeUsers.push({
              fullName: content.content,
              userId: content.userId,
            });
          }
        }
      });
    });

    return uniqBy(path(['userId']), activeUsers);
  }
  return [];
};

export const getAnnotationsCountForChannel = (state, channelId = null) =>
  pathOr([], ['annotations'], getNormalizedChannelById(state, channelId))
    .length;

export const getTotalAnnotationsCount = (state) =>
  Object.keys(pathOr({}, ['entities', 'annotations'], getScope(state))).length;

export const getInternalTeamChatChannelId = (state) => {
  const internalTeamChatChannel = Object.values(
    pathOr({}, ['entities', 'channels'], getScope(state)),
  ).find(({ type }) => type == CHANNEL_TYPES.PRIMARY);

  return pathOr(null, ['id'], internalTeamChatChannel);
};

export const getReqsReviewChatChannelId = (state) => {
  const reqsReviewChatChannel = Object.values(
    pathOr({}, ['entities', 'channels'], getScope(state)),
  ).find(({ type }) => type == CHANNEL_TYPES.ACTIONS);

  return pathOr(null, ['id'], reqsReviewChatChannel);
};

export const getMSCChatChannelId = (state) => {
  const mscChannel = Object.values(
    pathOr({}, ['entities', 'channels'], getScope(state)),
  ).find(({ type }) => type == CHANNEL_TYPES.MSC);

  return pathOr(null, ['id'], mscChannel);
};

export const getMscSpecialistForMscChannel = (state) => {
  const mscChannelId = getMSCChatChannelId(state);
  const mscChannel = getNormalizedChannelById(state, mscChannelId);
  return pathOr({}, ['mscSpecialist'], mscChannel);
};

export const getPennChannelId = (state) => {
  const pennChannel = Object.values(
    pathOr({}, ['entities', 'channels'], getScope(state)),
  ).find(({ type }) => type == CHANNEL_TYPES.PENN);

  return pathOr(null, ['id'], pennChannel);
};

const getNormalizedChannels = (state) =>
  pathOr([], ['entities', 'channels'], getScope(state));

export const sharedUserHasPermissionToUploadFiles = (state) => {
  const normalizedChannels = pathOr(
    [],
    ['entities', 'channels'],
    getScope(state),
  );
  return Boolean(
    Object.values(normalizedChannels).some(
      (channel) => channel.permissionToUploadFiles,
    ),
  );
};

export const sharedUserHasPermissionToDownloadFiles = (state) => {
  const normalizedChannels = pathOr(
    [],
    ['entities', 'channels'],
    getScope(state),
  );
  return Boolean(
    Object.values(normalizedChannels).some(
      (channel) => channel.permissionToDownloadFiles,
    ),
  );
};

export const getSharedUserHasAccessToPrimaryFile = createSelector(
  getNormalizedChannels,
  (normalizedChannels) =>
    Boolean(
      Object.values(normalizedChannels).some(
        (channel) => channel.accessToPrimaryFile,
      ),
    ),
);

const getSharesByChannelId = (state, channelId) => {
  const normalizedChannels = pathOr(
    [],
    ['entities', 'channels'],
    getScope(state),
  );

  if (!(channelId in normalizedChannels)) {
    return null;
  }

  const denormalizedChannel = denormalize(
    normalizedChannels[channelId],
    [channelSchema],
    getScope(state).entities,
  );
  const normalizedShares = pathOr([], ['shares'], denormalizedChannel);
  const denormalizedShares = denormalize(
    normalizedShares,
    [shareSchema],
    getScope(state).entities,
  );

  return denormalizedShares;
};

export const getShareExpirationByChannelIdForGroup = (
  state,
  channelId,
  groupId,
) => {
  const denormalizedShares = getSharesByChannelId(state, channelId);
  if (!denormalizedShares) {
    return null;
  }

  const relevantShareExpirationDates = denormalizedShares
    .filter(
      (share) =>
        share.accessRevokedOn === null && share.sharedWithGroupId === groupId,
    )
    .map((share) => share.expirationDate);

  if (relevantShareExpirationDates.length === 0) {
    return null;
  }

  return findFarthestExpiration(relevantShareExpirationDates);
};

export const isAnyShareToActiveGroupValid = (state, groupId) => {
  const shares = Object.values(
    pathOr([], ['entities', 'shares'], getScope(state)),
  );

  const relevantShares = shares.filter(
    (share) =>
      share.accessRevokedOn === null && share.sharedWithGroupId === groupId,
  );
  const expirationDates = relevantShares.map((share) =>
    share.expirationDate
      ? toMoment(share.expirationDate)
      : share.expirationDate,
  );
  const now = moment();

  return any(
    (expirationDate) => !expirationDate || now.isBefore(expirationDate),
  )(expirationDates);
};

export const getFarthestExpirationForChannel = (state, channelId) => {
  const denormalizedShares = getSharesByChannelId(state, channelId);
  if (!denormalizedShares) {
    return null;
  }

  const relevantShareExpirationDates = denormalizedShares
    .filter((share) => share.accessRevokedOn === null)
    .map((share) => share.expirationDate);

  if (relevantShareExpirationDates.length === 0) {
    return null;
  }

  return findFarthestExpiration(relevantShareExpirationDates);
};

export const isChannelAcessExpired = (state, channelId) => {
  const channelExpiration = getFarthestExpirationForChannel(state, channelId);
  return Date.now() > channelExpiration?.valueOf();
};

export default reduceReducers(entitiesReducer, reducer, initialState);
